import {
  ArrowRightIcon,
  Bars3Icon,
  BoltIcon,
  ChartBarIcon,
  CheckIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  StarIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: white;
  --max-width: 1560px;
  --primary: #ea580c;
  --primary-light: #fb923c;
  --success: #10b981;
  --warning: #f59e0b;
  --text-gray: #9ca3af;
`;

// Navigation Styles
const Navbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(10, 10, 15, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(234, 88, 12, 0.1);
  z-index: 1000;
  padding: 1rem 0;
`;

const NavContainer = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 900;
  color: white;
  cursor: pointer;

  img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary) 0%, #c2410c 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 1.25rem;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }

  button {
    background: none;
    border: none;
    color: var(--text-gray);
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: white;
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: var(--text-gray);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const NavPrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--primary-light);
    transform: translateY(-1px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: rgba(10, 10, 15, 0.98);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(234, 88, 12, 0.1);
    padding: 1rem 2rem;
    gap: 1rem;

    button {
      background: none;
      border: none;
      color: var(--text-gray);
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      padding: 0.75rem 0;
      text-align: left;

      &:hover {
        color: white;
      }
    }

    .mobile-actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);

      button {
        padding: 0.75rem 1.5rem;
        border-radius: 8px;
        text-align: center;

        &:first-child {
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        &:last-child {
          background: var(--primary);
          color: white;
          font-weight: 600;
        }
      }
    }
  }
`;

const Hero = styled.section`
  padding: 12rem 2rem 6rem;
  background: linear-gradient(135deg, var(--primary) 0%, #c2410c 100%);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`;

const HeroBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 2rem;

  span {
    background: white;
    color: var(--primary);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    margin-right: 0.5rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.8) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1.35rem;
  margin-bottom: 3rem;
  opacity: 0.95;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 4rem;
`;

const Button = styled(Link)<{ $variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  ${(props) =>
    props.$variant === 'secondary'
      ? `
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  `
      : `
    background: white;
    color: var(--primary);
    border: 2px solid white;
    
    &:hover {
      background: rgba(255, 255, 255, 0.95);
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    }
  `}
`;

const TrustSection = styled.div`
  text-align: center;
  opacity: 0.9;

  p {
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
`;

const TrustLogos = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;

  span {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const StatsBar = styled.section`
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const StatsContainer = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
`;

const StatItem = styled.div`
  text-align: center;

  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: var(--primary);
    margin: 0 auto 1rem;
  }
`;

const StatValue = styled.div`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--text-gray);
  font-size: 1rem;
`;

const ProblemSection = styled.section`
  padding: 8rem 2rem;
  background: rgba(255, 255, 255, 0.02);
`;

const SectionContainer = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`;

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const SectionTitle = styled.h2<{ $problem?: boolean }>`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${(props) => (props.$problem ? '#ef4444' : 'white')};

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDesc = styled.p`
  font-size: 1.15rem;
  color: var(--text-gray);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const ProblemList = styled.ul`
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
    font-size: 1.1rem;

    svg {
      width: 1.5rem;
      height: 1.5rem;
      color: #ef4444;
      flex-shrink: 0;
    }
  }
`;

const SolutionList = styled.ul`
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
    font-size: 1.1rem;

    svg {
      width: 1.5rem;
      height: 1.5rem;
      color: var(--success);
      flex-shrink: 0;
    }
  }
`;

const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  max-width: var(--max-width);
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--text-gray);
  margin-top: 1rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-8px);
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(234, 88, 12, 0.1);
  color: var(--primary);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const AgencySection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(
    180deg,
    rgba(234, 88, 12, 0.08) 0%,
    transparent 100%
  );
`;

const AgencyContainer = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const AgencyBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(234, 88, 12, 0.2);
  border: 1px solid rgba(234, 88, 12, 0.3);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 1.5rem;
`;

const AgencyFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const AgencyFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(234, 88, 12, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(234, 88, 12, 0.2);

  svg {
    width: 24px;
    height: 24px;
    color: var(--primary);
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 0.25rem;
  }

  span {
    font-size: 0.9rem;
    color: var(--text-gray);
  }
`;

const Pricing = styled.section`
  padding: 8rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  max-width: var(--max-width);
  margin: 0 auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  align-items: stretch;
`;

const PricingCard = styled.div<{ $featured?: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  padding: 2.5rem;
  border: ${(props) =>
    props.$featured
      ? '2px solid var(--primary)'
      : '1px solid rgba(255, 255, 255, 0.05)'};
  position: relative;
  display: flex;
  flex-direction: column;
  transform: ${(props) => (props.$featured ? 'scale(1.05)' : 'scale(1)')};

  @media (max-width: 1024px) {
    transform: scale(1);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -1rem;
  right: 2rem;
  background: var(--primary);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  color: var(--text-gray);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const Price = styled.div`
  margin-bottom: 1.5rem;

  .amount {
    font-size: 3rem;
    font-weight: 800;
  }

  .currency {
    font-size: 1.5rem;
    color: var(--text-gray);
  }

  .period {
    color: var(--text-gray);
    font-size: 0.875rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
  flex: 1;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    color: #d1d5db;

    svg {
      width: 1.25rem;
      height: 1.25rem;
      color: var(--success);
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  }
`;

const PricingButton = styled(Link)<{ $featured?: boolean }>`
  display: block;
  width: 100%;
  padding: 0.875rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  transition: all 0.2s;

  ${(props) =>
    props.$featured
      ? `
    background: var(--primary);
    color: white;
    
    &:hover {
      background: var(--primary-light);
      transform: translateY(-2px);
    }
  `
      : `
    background: transparent;
    color: var(--primary);
    border: 2px solid var(--primary);
    
    &:hover {
      background: rgba(234, 88, 12, 0.1);
      transform: translateY(-2px);
    }
  `}
`;

const TestimonialsSection = styled.section`
  padding: 8rem 2rem;
  background: rgba(255, 255, 255, 0.02);
`;

const TestimonialsGrid = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s;

  &:hover {
    border-color: rgba(234, 88, 12, 0.3);
    transform: translateY(-5px);
  }
`;

const TestimonialStars = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;

  svg {
    width: 20px;
    height: 20px;
    color: #fbbf24;
  }
`;

const TestimonialQuote = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  color: #e5e7eb;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-light) 100%
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 1rem;
  }

  span {
    font-size: 0.875rem;
    color: var(--text-gray);
  }
`;

const CTASection = styled.section`
  padding: 8rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, var(--primary) 0%, #c2410c 100%);
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

// Footer Styles
const Footer = styled.footer`
  background: #000;
  padding: 4rem 2rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterContainer = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterCol = styled.div`
  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
  }

  p {
    color: var(--text-gray);
    font-size: 0.875rem;
    line-height: 1.6;
    margin-top: 1rem;
  }

  button {
    display: block;
    background: none;
    border: none;
    color: var(--text-gray);
    font-size: 0.875rem;
    padding: 0.5rem 0;
    cursor: pointer;
    text-align: left;
    transition: color 0.2s;

    &:hover {
      color: white;
    }
  }
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  color: var(--text-gray);
  font-size: 0.875rem;
`;

const Home: FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const plans = [
    {
      name: 'Free',
      description: 'Get started with basic features',
      price: 0,
      period: 'forever',
      features: [
        'Up to 2 users',
        '500 products',
        '3 marketplace connections',
        '100 MB storage',
        'Community support',
        'Basic analytics',
      ],
    },
    {
      name: 'Starter',
      description: 'Perfect for small teams',
      price: 49,
      period: 'month',
      features: [
        'Up to 5 users',
        '2,500 products',
        '10 marketplace connections',
        '500 MB storage',
        'Email support',
        'API access',
        'Webhooks',
        'Advanced analytics',
      ],
    },
    {
      name: 'Professional',
      description: 'For growing businesses',
      price: 149,
      period: 'month',
      featured: true,
      features: [
        'Up to 20 users',
        '25,000 products',
        '50 marketplace connections',
        '5 GB storage',
        'Priority support',
        'AI-powered optimization',
        'Advanced integrations',
        '100K API calls/day',
        'Custom branding',
      ],
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions',
      price: 499,
      period: 'month',
      features: [
        'Unlimited users',
        'Unlimited products',
        'Unlimited connections',
        'Unlimited storage',
        'Dedicated support',
        'White-label solution',
        'SSO & advanced security',
        'Unlimited API calls',
        'Custom development',
      ],
    },
  ];

  const testimonials = [
    {
      quote:
        'Marketeer Commerce transformed our multi-channel selling. We manage 5 brands and 15 marketplace connections from one dashboard. Game changer!',
      author: 'Sarah Chen',
      role: 'Director of E-commerce',
      company: 'GlobeTech',
      avatar: 'SC',
    },
    {
      quote:
        'The AI-powered inventory optimization saved us from stockouts and reduced our holding costs by 30%. ROI in the first month!',
      author: 'Michael Roberts',
      role: 'Operations Manager',
      company: 'Urban Supply Co.',
      avatar: 'MR',
    },
    {
      quote:
        'As an agency managing 50+ client stores, the multi-tenant architecture is perfect. Each client gets their own workspace with full isolation.',
      author: 'Jessica Tang',
      role: 'Agency Owner',
      company: 'Digital Commerce Partners',
      avatar: 'JT',
    },
  ];

  return (
    <Container>
      {/* Navigation */}
      <Navbar>
        <NavContainer>
          <NavLogo onClick={() => navigate('/')}>
            <LogoIcon>M</LogoIcon>
            <span>MARKETEER</span>
          </NavLogo>

          <NavLinks>
            <button onClick={() => scrollToSection('features')}>
              Features
            </button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('testimonials')}>
              Testimonials
            </button>
          </NavLinks>

          <NavActions>
            <NavButton onClick={() => navigate('/login')}>Log in</NavButton>
            <NavPrimaryButton onClick={() => navigate('/register')}>
              Get Started
            </NavPrimaryButton>
          </NavActions>

          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
          </MobileMenuButton>
        </NavContainer>

        <MobileMenu $isOpen={mobileMenuOpen}>
          <button onClick={() => scrollToSection('features')}>Features</button>
          <button onClick={() => scrollToSection('pricing')}>Pricing</button>
          <button onClick={() => scrollToSection('testimonials')}>
            Testimonials
          </button>
          <div className="mobile-actions">
            <button onClick={() => navigate('/login')}>Log in</button>
            <button onClick={() => navigate('/register')}>Get Started</button>
          </div>
        </MobileMenu>
      </Navbar>

      {/* Hero Section */}
      <Hero>
        <HeroContent>
          <HeroBadge>
            <span>New</span> Multi-Marketplace Sync 2.0 is here
          </HeroBadge>
          <HeroTitle>
            Stop Juggling Platforms. <br />
            <GradientText>Start Scaling.</GradientText>
          </HeroTitle>
          <HeroSubtitle>
            The all-in-one e-commerce platform that unifies Amazon, eBay, Etsy,
            and more. Automate inventory, pricing, and orders with AI-powered
            sync across all marketplaces.
          </HeroSubtitle>
          <CTAButtons>
            <Button to="/register">
              Start Free Trial <ArrowRightIcon />
            </Button>
            <Button to="/login" $variant="secondary">
              Sign In
            </Button>
          </CTAButtons>
          <TrustSection>
            <p>Trusted by e-commerce teams worldwide</p>
            <TrustLogos>
              <span>Amazon</span>
              <span>eBay</span>
              <span>Etsy</span>
              <span>Shopify</span>
              <span>WooCommerce</span>
            </TrustLogos>
          </TrustSection>
        </HeroContent>
      </Hero>

      {/* Stats Bar */}
      <StatsBar>
        <StatsContainer>
          <StatItem>
            <UserGroupIcon />
            <StatValue>10,000+</StatValue>
            <StatLabel>Active Users</StatLabel>
          </StatItem>
          <StatItem>
            <RocketLaunchIcon />
            <StatValue>5M+</StatValue>
            <StatLabel>Products Synced</StatLabel>
          </StatItem>
          <StatItem>
            <ChartBarIcon />
            <StatValue>127%</StatValue>
            <StatLabel>Avg. Revenue Increase</StatLabel>
          </StatItem>
          <StatItem>
            <StarIcon />
            <StatValue>4.9/5</StatValue>
            <StatLabel>Customer Rating</StatLabel>
          </StatItem>
        </StatsContainer>
      </StatsBar>

      {/* Problem/Solution Section */}
      <ProblemSection>
        <SectionContainer>
          <ProblemGrid>
            <div>
              <SectionTitle $problem>E-commerce is fragmented.</SectionTitle>
              <SectionDesc>
                You're managing multiple marketplaces, inventory is out of sync,
                and manual updates are eating your time. The old way of selling
                online is chaotic, error-prone, and impossible to scale.
              </SectionDesc>
              <ProblemList>
                <li>
                  <XMarkIcon /> Disconnected marketplace accounts
                </li>
                <li>
                  <XMarkIcon /> Manual inventory & price updates
                </li>
                <li>
                  <XMarkIcon /> Order processing nightmares
                </li>
                <li>
                  <XMarkIcon /> No unified analytics
                </li>
              </ProblemList>
            </div>
            <div>
              <SectionTitle>Marketeer is the solution.</SectionTitle>
              <SectionDesc>
                Unify all your marketplaces into one intelligent platform. Let
                AI handle synchronization while you focus on growth and
                strategy.
              </SectionDesc>
              <SolutionList>
                <li>
                  <CheckIcon /> All-in-one unified dashboard
                </li>
                <li>
                  <CheckIcon /> Real-time multi-channel sync
                </li>
                <li>
                  <CheckIcon /> AI-powered pricing & inventory
                </li>
                <li>
                  <CheckIcon /> Automated order routing
                </li>
              </SolutionList>
            </div>
          </ProblemGrid>
        </SectionContainer>
      </ProblemSection>

      {/* Features Section */}
      <FeaturesSection id="features">
        <SectionHeader>
          <SectionTitle>Everything you need to scale</SectionTitle>
          <SectionSubtitle>
            Powerful tools for every e-commerce challenge
          </SectionSubtitle>
        </SectionHeader>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>
              <CloudArrowUpIcon />
            </FeatureIcon>
            <h3>Multi-Channel Sync</h3>
            <p>
              Automatic synchronization across Amazon, eBay, Etsy, Shopify, and
              WooCommerce. Update once, sync everywhere.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <CpuChipIcon />
            </FeatureIcon>
            <h3>AI Inventory Management</h3>
            <p>
              Predictive stock optimization prevents overselling and stockouts.
              Smart reorder points based on demand patterns.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <CurrencyDollarIcon />
            </FeatureIcon>
            <h3>Dynamic Pricing</h3>
            <p>
              AI-powered competitive pricing across marketplaces. Maximize
              margins while staying competitive.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <ChartBarIcon />
            </FeatureIcon>
            <h3>Unified Analytics</h3>
            <p>
              Real-time dashboards combining data from all channels. Make
              data-driven decisions with confidence.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <BoltIcon />
            </FeatureIcon>
            <h3>Order Automation</h3>
            <p>
              Automatic order routing, fulfillment tracking, and customer
              notifications across all platforms.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <ShieldCheckIcon />
            </FeatureIcon>
            <h3>Enterprise Security</h3>
            <p>
              Bank-level encryption, multi-tenant isolation, and SOC 2 compliant
              infrastructure.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <GlobeAltIcon />
            </FeatureIcon>
            <h3>Global Marketplace Network</h3>
            <p>
              Connect to 15+ major marketplaces worldwide. Expand
              internationally with localized pricing and shipping.
            </p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <UserGroupIcon />
            </FeatureIcon>
            <h3>Team Collaboration</h3>
            <p>
              Role-based access control, activity logs, and collaborative
              workflows for your entire team.
            </p>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      {/* Agency Section */}
      <AgencySection>
        <AgencyContainer>
          <div>
            <AgencyBadge>For Agencies & Enterprises</AgencyBadge>
            <SectionTitle>
              One Platform.
              <br />
              <GradientText>Unlimited Stores.</GradientText>
            </SectionTitle>
            <SectionDesc>
              Whether you're an agency managing multiple client stores or an
              enterprise with several brands, Marketeer's multi-tenant
              architecture gives you complete control from a single dashboard.
            </SectionDesc>
            <AgencyFeatures>
              <AgencyFeature>
                <CheckIcon />
                <div>
                  <strong>Agency Dashboard</strong>
                  <span>
                    Bird's-eye view of all client accounts and performance
                    metrics
                  </span>
                </div>
              </AgencyFeature>
              <AgencyFeature>
                <CheckIcon />
                <div>
                  <strong>Client Workspaces</strong>
                  <span>
                    Isolated environments for each store with custom branding
                  </span>
                </div>
              </AgencyFeature>
              <AgencyFeature>
                <CheckIcon />
                <div>
                  <strong>White-Label Reports</strong>
                  <span>
                    Generate branded reports for clients with your agency's logo
                  </span>
                </div>
              </AgencyFeature>
            </AgencyFeatures>
          </div>
          <div>
            <FeatureCard>
              <FeatureIcon>
                <UserGroupIcon />
              </FeatureIcon>
              <h3>Multi-Tenant Architecture</h3>
              <p>
                Manage unlimited stores from one account. Each tenant gets
                complete data isolation and custom settings.
              </p>
              <div
                style={{
                  marginTop: '2rem',
                  padding: '1rem',
                  background: 'rgba(234, 88, 12, 0.1)',
                  borderRadius: '8px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    textAlign: 'center',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: 'var(--primary)',
                      }}
                    >
                      50+
                    </div>
                    <div
                      style={{ fontSize: '0.85rem', color: 'var(--text-gray)' }}
                    >
                      Stores
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: 'var(--primary)',
                      }}
                    >
                      200+
                    </div>
                    <div
                      style={{ fontSize: '0.85rem', color: 'var(--text-gray)' }}
                    >
                      Marketplaces
                    </div>
                  </div>
                </div>
              </div>
            </FeatureCard>
          </div>
        </AgencyContainer>
      </AgencySection>

      {/* Pricing Section */}
      <Pricing id="pricing">
        <SectionHeader>
          <SectionTitle>Simple, Transparent Pricing</SectionTitle>
          <SectionSubtitle>
            Choose the perfect plan for your business. All plans include 14-day
            free trial.
          </SectionSubtitle>
        </SectionHeader>

        <PricingGrid>
          {plans.map((plan) => (
            <PricingCard key={plan.name} $featured={plan.featured}>
              {plan.featured && <PopularBadge>Most Popular</PopularBadge>}

              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>

              <Price>
                <div>
                  <span className="currency">$</span>
                  <span className="amount">{plan.price}</span>
                </div>
                <div className="period">per {plan.period}</div>
              </Price>

              <FeatureList>
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <CheckIcon />
                    <span>{feature}</span>
                  </li>
                ))}
              </FeatureList>

              <PricingButton to="/register" $featured={plan.featured}>
                {plan.price === 0 ? 'Get Started' : 'Start Free Trial'}
              </PricingButton>
            </PricingCard>
          ))}
        </PricingGrid>
      </Pricing>

      {/* Testimonials */}
      <TestimonialsSection>
        <SectionHeader>
          <SectionTitle>Loved by e-commerce professionals</SectionTitle>
          <SectionSubtitle>See what our customers have to say</SectionSubtitle>
        </SectionHeader>
        <TestimonialsGrid>
          {testimonials.map((testimonial, idx) => (
            <TestimonialCard key={idx}>
              <TestimonialStars>
                {[...Array(5)].map((_, i) => (
                  <StarSolid key={i} />
                ))}
              </TestimonialStars>
              <TestimonialQuote>"{testimonial.quote}"</TestimonialQuote>
              <TestimonialAuthor>
                <AuthorAvatar>{testimonial.avatar}</AuthorAvatar>
                <AuthorInfo>
                  <strong>{testimonial.author}</strong>
                  <span>
                    {testimonial.role}, {testimonial.company}
                  </span>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </TestimonialsSection>

      {/* Final CTA */}
      <CTASection>
        <CTAContent>
          <SectionTitle>Ready to transform your e-commerce?</SectionTitle>
          <SectionDesc style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Join thousands of businesses already scaling with Marketeer
            Commerce. Start your 14-day free trial today—no credit card
            required.
          </SectionDesc>
          <CTAButtons>
            <Button to="/register">
              Start Free Trial <ArrowRightIcon />
            </Button>
            <Button to="/login" $variant="secondary">
              Sign In
            </Button>
          </CTAButtons>
        </CTAContent>
      </CTASection>

      {/* Footer */}
      <Footer>
        <FooterContainer>
          <FooterCol>
            <NavLogo onClick={() => navigate('/')}>
              <LogoIcon>M</LogoIcon>
              <span>MARKETEER</span>
            </NavLogo>
            <p>
              The AI-powered e-commerce platform for businesses managing
              multiple marketplaces. Sync, automate, and scale your online sales
              effortlessly.
            </p>
          </FooterCol>
          <FooterCol>
            <h4>Product</h4>
            <button onClick={() => scrollToSection('features')}>
              Features
            </button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => navigate('/products')}>Products</button>
            <button onClick={() => navigate('/marketplaces')}>
              Marketplaces
            </button>
          </FooterCol>
          <FooterCol>
            <h4>Company</h4>
            <button>About</button>
            <button>Careers</button>
            <button>Blog</button>
            <button>Contact</button>
          </FooterCol>
          <FooterCol>
            <h4>Legal</h4>
            <button>Privacy Policy</button>
            <button>Terms of Service</button>
            <button>Security</button>
            <button>Compliance</button>
          </FooterCol>
        </FooterContainer>
        <FooterBottom>
          <p>
            &copy; {new Date().getFullYear()} Marketeer Commerce. All rights
            reserved.
          </p>
        </FooterBottom>
      </Footer>
    </Container>
  );
};

export default Home;
